<script setup lang="ts">
import { Icon, IconName } from '@exfluential/ui';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

type Props = {
  images: { url: string; position: number }[];
  autoRotate?: boolean;
  intervalSeconds?: number;
};

const props = withDefaults(defineProps<Props>(), {
  autoRotate: true,
  intervalSeconds: 5,
});

const sortedImages = computed(() =>
  [...props.images].sort((a, b) => a.position - b.position),
);

const activeIndex = ref(0);
const isPaused = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;

const goTo = (index: number) => {
  const count = sortedImages.value.length;
  if (count === 0) return;
  activeIndex.value = ((index % count) + count) % count;
};

const next = () => goTo(activeIndex.value + 1);
const previous = () => goTo(activeIndex.value - 1);

const startTimer = () => {
  if (!props.autoRotate || sortedImages.value.length < 2) return;
  timer = setInterval(() => {
    if (!isPaused.value) next();
  }, props.intervalSeconds * 1000);
};

onMounted(startTimer);
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div
    v-if="sortedImages.length > 0"
    class="group relative aspect-[4/1] w-full overflow-hidden rounded-lg"
    @mouseenter="isPaused = true"
    @mouseleave="isPaused = false"
  >
    <img
      v-for="(image, index) of sortedImages"
      :key="image.position"
      :src="image.url"
      alt=""
      class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
      :class="index === activeIndex ? 'opacity-100' : 'opacity-0'"
    />

    <template v-if="sortedImages.length > 1">
      <button
        type="button"
        class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-surface/80 p-2 opacity-0 transition-opacity group-hover:opacity-100"
        :aria-label="$t('common.previous')"
        @click="previous"
      >
        <Icon :name="IconName.CHEVRON_LEFT" class="h-4 w-4" />
      </button>
      <button
        type="button"
        class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-surface/80 p-2 opacity-0 transition-opacity group-hover:opacity-100"
        :aria-label="$t('common.next')"
        @click="next"
      >
        <Icon :name="IconName.CHEVRON_RIGHT" class="h-4 w-4" />
      </button>

      <div class="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
        <button
          v-for="(image, index) of sortedImages"
          :key="`dot-${image.position}`"
          type="button"
          class="h-2 rounded-full transition-all"
          :class="
            index === activeIndex ? 'bg-primary w-6' : 'bg-surface/70 w-2'
          "
          :aria-label="`${index + 1}`"
          @click="goTo(index)"
        />
      </div>
    </template>
  </div>
</template>
